/** Localized test content for a single language */
export interface TestDataLocale {
  question: string;
  options: string[];
  correctIndex: number;
  hint?: string;
}

/** test_data column: { "ru": {...}, "en": {...} } */
export type TestData = Record<string, TestDataLocale>;

export interface Card {
  id?: string;
  front: string;
  back: string;
  /** Language code for the front/term side (e.g. "en", "es"). Used for TTS, AI. */
  frontLang?: string;
  /** Language code for the back/definition side (e.g. "en", "es"). Used for TTS, AI. */
  backLang?: string;
  hint?: string;
  /** Short learner-friendly definition (AI-enriched, CEFR A1–B2 style). */
  definition?: string | null;
  /** Natural example sentence (AI-enriched). */
  example?: string | null;
  /** Contextual image prompt for AI generation (e.g. 'restaurant_receipt' for 'bill' in food context). Underscores, no spaces. */
  imagePrompt?: string;
  imageUrl?: string | null;
  /** Pre-generated Neural TTS audio URL for front/term side (Supabase Storage). */
  frontAudioUrl?: string | null;
  /** Pre-generated Neural TTS audio URL for back/translation side (Supabase Storage). */
  backAudioUrl?: string | null;
  /** Locale used for the cached front-side TTS asset (e.g. "en-US"). */
  frontAudioLocale?: string | null;
  /** Locale used for the cached back-side TTS asset (e.g. "es-ES"). */
  backAudioLocale?: string | null;
  cardType?: "grammar" | "vocabulary";
  level?: string;
  aiExplanation?: string | null;
  testData?: TestData | null;
  /** SRS (SM-2) fields */
  repetition_count?: number;
  easiness_factor?: number;
  interval_days?: number;
  next_review?: string | null;
}

export type ShareAccess = "read" | "write";

export interface Folder {
  id: string;
  user_id: string;
  title: string;
  created_at: string;
  tags?: string[];
  shareToken?: string | null;
}

export type DeckType = "words" | "cards" | "tests";

/** Supported deck/study language codes (for flashcards, TTS, AI generation) */
export type DeckLang = "en" | "es" | "fr" | "de" | "it" | "nl" | "pl" | "pt-BR" | "tr" | "ko" | "ru" | "uk";

/** Enabled study modes for tab filtering (Cards, Learn, Test, Match, Philology). Stored as study_modes JSONB in DB. */
export interface StudyModes {
  cards: boolean;
  learn: boolean;
  test: boolean;
  match: boolean;
  philology: boolean;
}

export interface Deck {
  id: string;
  title: string;
  description?: string;
  lang: DeckLang;
  emoji: string;
  cards: Card[];
  level?: string;
  /** Sidebar section: words, cards, tests */
  type?: DeckType;
  deckType?: string;
  /** Enabled modes for tab filtering. Maps to study_modes JSONB in DB. */
  studyModes?: StudyModes;
  shareToken?: string | null;
  shareAccess?: ShareAccess | null;
  /** ISO timestamp for time-based grouping (Today, This week, Older) */
  created_at?: string;
  /** Card count for list views when cards are not loaded (performance). */
  cardCount?: number;
  /** From deck_summaries: available for Learn mode (client or server). */
  learnableCount?: number;
  /** From deck_summaries: available for Test mode (client or server). */
  testableCount?: number;
  /** From deck_summaries: available for Games mode (client or server). */
  gamesCount?: number;
}

export type ViewType = "home" | "library" | "flashcards" | "test" | "upload" | "dashboard" | "gamification";

export interface AppState {
  decks: Deck[];
  folders: Folder[];
  currentDeckId: string | null;
  currentView: ViewType;
  cardIndex: number;
  cardFlipped: boolean;
  points: number;
  crystals: number;
  streak: number;
  lastActivityDate: string | null; // YYYY-MM-DD for streak calculation
  uiLang: string;
  theme: "dark" | "light" | "system";
  aiModel: string;
  /** Multiple API keys for failover (rate limit / quota). */
  pollinationsKey: string[];
  /** Multiple API keys for failover (rate limit / quota). */
  geminiKey: string[];
  loading: boolean;
}

export const LANG_FLAGS: Record<string, string> = {
  en: "🇬🇧",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  it: "🇮🇹",
  nl: "🇳🇱",
  pl: "🇵🇱",
  "pt-BR": "🇧🇷",
  tr: "🇹🇷",
  ko: "🇰🇷",
  ru: "🇷🇺",
  uk: "🇺🇦",
};

export const LANG_NAMES: Record<string, string> = {
  en: "🇬🇧 English",
  es: "🇪🇸 Español",
  fr: "🇫🇷 Français",
  de: "🇩🇪 Deutsch",
  it: "🇮🇹 Italiano",
  nl: "🇳🇱 Nederlands",
  pl: "🇵🇱 Polski",
  "pt-BR": "🇧🇷 Português (BR)",
  tr: "🇹🇷 Türkçe",
  ko: "🇰🇷 한국어",
  ru: "🇷🇺 Русский",
  uk: "🇺🇦 Українська",
};

/** Language codes for deck creation / AI generation (study languages) */
export const STUDY_LANGUAGE_CODES: DeckLang[] = ["en", "es", "fr", "de", "it", "nl", "pl", "pt-BR", "tr", "ko", "ru", "uk"];

/** Full English names for AI prompts (Gemini, etc.) */
export const GEMINI_LANG_NAMES: Record<string, string> = {
  en: "English", es: "Spanish", fr: "French", de: "German", it: "Italian", nl: "Dutch",
  pl: "Polish", "pt-BR": "Portuguese (Brazil)", tr: "Turkish", ko: "Korean", ru: "Russian", uk: "Ukrainian",
};

export const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

/** Empty — new users get no demo decks. Kept for type compatibility. */
export const DEFAULT_DECKS: Deck[] = [];
